<template>
    <div>
        <a
            :href="VITE_WIN_DASHBOARD_URL"
            target="_blank"
            class="text-blue-600 hover:underline font-bold"
        >
            Back to Dashboard
        </a>

        <h2 class="title my-4">
            Change Plan
        </h2>

        <div class="flex flex-col xl:gap-48 lg:gap-32 lg:flex-row-reverse lg:items-start lg:justify-between">
            <!-- SIDEBAR -->
            <div class="col flex flex-col gap-2 min-w-[350px]">
                <LineItem
                    v-if="currentSubItem"
                    label="Current Plan"
                    :description="`${currentSubItem.price.product.name} - ${currentSubItem.price.recurring.interval === 'month' ? 'monthly' : 'yearly'}`"
                    :price="currentSubItem.price.unit_amount"
                    class="bg-white rounded-md px-3 py-4 mb-2"
                />

                <OrderDetails
                    class="bg-white rounded-md px-3 py-4 mb-2"
                />
            </div>

            <!-- MAIN -->
            <div class="flex-auto h-full">
                <template v-if="state.upgradeComplete">
                    <div class="flex flex-col gap-3 mt-4 lg:mt-0">
                        <p>
                            Your membership plan has been successfully changed.
                        </p>
                        <p>
                            <a
                                :href="VITE_WIN_DASHBOARD_URL"
                                target="_blank"
                                class="text-blue-600 hover:underline font-bold"
                            >Click here to return to the WIN Reality Dashboard</a>
                        </p>
                    </div>
                </template>
                <template v-else-if="state.authenticationReady">
                    <!-- NOT LOGGED IN -->
                    <div
                        v-if="!user.isLoggedIn"
                        class="flex flex-col gap-3 mt-4 lg:mt-0"
                    >
                        <p>
                            You must be logged in to change your plan.
                        </p>
                        <p>
                            <a
                                :href="VITE_WIN_DASHBOARD_URL"
                                target="_blank"
                                class="text-blue-600 hover:underline font-bold"
                            >Click here to login</a>
                        </p>
                    </div>

                    <!-- IF SELECTED PLAN IS SAME AS CORE SUB -->
                    <template v-else-if="mustSelectNewPlan">
                        <div class="flex flex-col gap-3 mt-4 lg:mt-0">
                            <p>The plan you selected previously is not eligible for an upgrade or downgrade as it is the same as your existing plan.</p>
                            <p>If you want to change your plan's billing period, please do so from the
                                <a
                                    :href="VITE_WIN_DASHBOARD_URL"
                                    target="_blank"
                                    class="text-blue-600 hover:underline font-bold"
                                >WIN Reality Dashboard</a>
                            </p>
                        </div>
                    </template>

                    <!-- NO CORE SUBSCRIPTION -->
                    <template v-else-if="!state.coreSubscription">
                        <p>No Core Subscription found</p>
                        <p>
                            You must have a current membership to change your plan.
                        </p>
                    </template>

                    <!-- MAIN -->
                    <template v-else>
                        <AppAccordion
                            label="Select Equipment"
                            prefix="1."
                            class="border-t-2 border-gray-400"
                            :model-value="steps.isCurrent('equipment')"
                            :disabled="steps.isBefore('equipment')"
                            :complete="steps.isAfter('equipment')"
                            @update:model-value="(open: boolean) => open && steps.goTo('equipment')"
                        >
                            <EquipmentStep
                                @complete="cart.requireShipping ? steps.goToNext() : steps.goTo('paymentMethod')"
                            />
                        </AppAccordion>

                        <AppAccordion
                            v-show="cart.requireShipping"
                            label="Shipping Address"
                            prefix="2."
                            class="border-t-2 border-gray-400"
                            :model-value="steps.isCurrent('shipping')"
                            :disabled="steps.isBefore('shipping')"
                            :complete="steps.isAfter('shipping')"
                            @update:model-value="(open: boolean) => open && steps.goTo('shipping')"
                        >
                            <AddressInput
                                class="mb-6"
                                :address="store.$state.shippingAddress"
                                :phone="store.$state.phone"
                                :full-name="store.$state.shippingFullName"
                                @update="handleAddressUpdate"
                            >
                            </AddressInput>
                        </AppAccordion>

                        <AppAccordion
                            label="Payment Method"
                            :prefix="cart.requireShipping ? '3.' : '2.'"
                            class="border-t-2 border-gray-400"
                            :model-value="steps.isCurrent('paymentMethod')"
                            :disabled="steps.isBefore('paymentMethod')"
                            :complete="steps.isAfter('paymentMethod')"
                            @update:model-value="(open: boolean) => open && steps.goTo('paymentMethod')"
                        >
                            <SelectPaymentMethod
                                v-if="state.paymentMethods.length && !state.showPaymentForm"
                                v-model="state.selectedPaymentMethodId"
                                :paymentMethods="state.paymentMethods"
                                @new="state.showPaymentForm = true"
                                @update:modelValue="steps.goToNext()"
                            >
                            </SelectPaymentMethod>

                            <StripeElements
                                v-if="state.showPaymentForm || (!state.paymentMethods.length && steps.isCurrent('paymentMethod'))"
                                :handler="handleAttatchPaymentMethod"
                                :validatorFn="prePaymentValidator"
                                buttonText="Save Payment"
                                :disableAddress="cart.requireShipping"
                                disableCoupon
                                disableWallet
                                hideFooter
                            >
                                <template
                                    v-if="state.paymentMethods.length"
                                    #buttons
                                >
                                    <button
                                        type="button"
                                        class="button button--secondary"
                                        @click="state.showPaymentForm = false"
                                    >
                                        Cancel
                                    </button>
                                </template>
                            </StripeElements>
                        </AppAccordion>

                        <AppAccordion
                            label="Confirm Membership Change"
                            :prefix="cart.requireShipping ? '4.' : '3.'"
                            class="border-t-2 border-gray-400"
                            :model-value="steps.isCurrent('confirmMembershipChange')"
                            :disabled="steps.isBefore('confirmMembershipChange')"
                            :complete="steps.isAfter('confirmMembershipChange')"
                            @update:model-value="(open: boolean) => open && steps.goTo('confirmMembershipChange')"
                        >

                            <div class="bg-white rounded-md p-5 text-base flex flex-col gap-3">
                                <div class="font-bold text-lg">
                                    Plan Change Summary
                                </div>
                                <!-- UPGRADE -->
                                <template v-if="cart.selectedPlan?.group_key !== 'standard'">
                                    <p>
                                        <span class="font-bold text-primary-500">Congratulations!</span>
                                        You are about to upgrade your membership to take full advantage of all that WIN has to offer.
                                    </p>
                                    <p>
                                        Your current plan will be replaced immediately and the remaining time will be prorated and applied to your new plan cost.
                                    </p>
                                </template>

                                <!-- DOWNGRADE -->
                                <template v-if="cart.selectedPlan?.group_key === 'standard'">
                                    <p>
                                        We're sorry your current plan didn't work out for you, but are excited you're planning to keep using WIN!
                                        You can always come back to the Dashboard to upgrade again when you are ready.
                                        Keep a look out for new features, training content, and game modes down the road.
                                    </p>
                                    <p>
                                        Your current plan will be replaced immediately and the remaining time will be prorated and applied to your new plan cost.
                                    </p>
                                </template>
                                <LineItem
                                    v-for="item in state.coreSubscription.items"
                                    :key="item.id"
                                    label="Current Plan"
                                    :description="`${item.price.product.name} - ${item.price.recurring.interval === 'month' ? 'monthly' : 'yearly'}`"
                                    :price="item.price.unit_amount"
                                />
                                <LineItem
                                    label="New Plan"
                                    :description="`WIN Reality - ${cart.selectedPlan?.label} Membership - ${cart.selectedPlan?.billing_interval}`"
                                    :price="cart.selectedPlan?.price"
                                />
                            </div>

                            <ErrorMessage
                                v-if="state.error"
                                class="my-4"
                            >
                                {{ state.error }}
                            </ErrorMessage>

                            <div class="flex flex-col gap-4 mt-4">
                                <button
                                    type="button"
                                    class="button button--primary"
                                    key="btn-next"
                                    @click="handleSubscriptionChange"
                                >
                                    Confirm Membership Change
                                </button>
                            </div>
                        </AppAccordion>
                    </template>
                </template>
            </div>

            <AppLoading v-if="!state.authenticationReady || state.isLoading" />

            <!-- MODALS -->
            <ClaimScopes
                v-if="state.showScopeClaimModal"
                v-model="state.showScopeClaimModal"
                :scopes="['sensitive']"
                @scopes="state.claimScopeFn"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from '@/store/checkout'
import { useUserStore } from '@/store/user'
import { useCartStore } from '@/store/cart'
import OrderDetails from '@/components/Shared/OrderDetails.vue'
import EquipmentStep from '@/components/Shared/EquipmentStep.vue'
import AppAccordion from '@/components/App/AppAccordion.vue'
import { createHardwareOrder } from '@/api/memberships'
import { computed, reactive, onMounted } from 'vue'
import StripeElements from '@/components/Shared/StripeElements.vue'
import SelectPaymentMethod from '@/components/PaymentMethod/SelectPaymentMethod.vue'
import ClaimScopes from '@/components/Account/ClaimScopes.vue'
import LineItem from '../Shared/LineItem.vue'
import { useStepper } from '@vueuse/core'
import AppLoading from '../App/AppLoading.vue'
import AddressInput from '../Form/AddressInput.vue'
import ErrorMessage from '../App/ErrorMessage.vue'

const VITE_WIN_DASHBOARD_URL = import.meta.env.VITE_WIN_DASHBOARD_URL as string

const store = useCheckoutStore()
const user = useUserStore()
const cart = useCartStore()
const steps = useStepper(['equipment', 'shipping', 'paymentMethod', 'confirmMembershipChange'])

onMounted(async() => {
    const params = new URLSearchParams(window.location.search)
    const refresh_token = params.get('refresh_token') || undefined
    const username = params.get('username') || undefined
    await user.getInitialState({ refresh_token, username })
    await handleLoginComplete()
    state.authenticationReady = true
})

const state = reactive({
    subscriptionComplete: false,
    equipmentComplete: false,
    authenticationReady: false,
    coreSubscription: null as UserSubscriptionResponse | null,
    paymentMethods: [] as stripe.paymentMethod.PaymentMethod[],
    selectedPaymentMethodId: null as string | null,
    showPaymentForm: false,
    showScopeClaimModal: false,
    isLoading: false,
    error: null as string | null,
    claimScopeFn: null as (() => Promise<any>) | null,
    upgradeComplete: true,
})

const currentSubItem = computed(() => {
    return state.coreSubscription?.items?.[0]
})

const mustSelectNewPlan = computed(() => {
    if (!cart.selectedPlan) {
        return true
    }
    const selectedPlanPriceId = cart.selectedPlan?.stripe_price_id
    const coreSubscriptionPriceId = currentSubItem.value?.price?.id
    return (selectedPlanPriceId === coreSubscriptionPriceId) && !!selectedPlanPriceId
})

const handleAddressUpdate = (data: { address: AddressFields, phone: string, fullName: string }) => {
    store.$patch({
        shippingAddress: data.address,
        phone: data.phone,
        shippingFullName: data.fullName,
    })
    steps.goToNext()
}

const handleLoginComplete = async() => {
    if (user.isLoggedIn) {
        try {
            const coreSubscription = await user.fetchCoreSubscription()
            state.coreSubscription = coreSubscription
            if (user.account?.payment_methods?.length) {
                state.paymentMethods = user.account.payment_methods
            }
            const defaultPaymentMethodId = user.account?.invoice_settings?.default_payment_method?.id
            const defaultPaymentMethod = state.paymentMethods.find((pm) => pm.id === defaultPaymentMethodId)
            const billingAddress = defaultPaymentMethod?.billing_details?.address
            const shippingAddress = user.account?.address

            store.$patch({
                accountValidated: true,
                email: user.user?.email || store.$state.email,
                fullName: `${user.user?.first_name || ''} ${user.user?.last_name || ''}`.trim(),
                shippingFullName: `${user.user?.first_name || ''} ${user.user?.last_name || ''}`.trim(),
                billingAddress: billingAddress || shippingAddress || store.$state.billingAddress,
                shippingAddress: shippingAddress || billingAddress || store.$state.billingAddress,
                phone: defaultPaymentMethod?.billing_details?.phone || store.$state.phone,
            })

            cart.$patch({
                planOptions: cart.planOptions.filter((plan) => plan.stripe_price_id === cart.$state.plan),
            })

            steps.goTo('equipment')
        } catch (e) {
            console.error(e)
        }
    } else {
        // user is not logged in
    }
}

const handleEquipmentPurchase = async(paymentMethodId: string) => {
    const params = store.getCheckoutParams()
    const orderParams = {
        ...params,
        payment_method: paymentMethodId || store.$state.paymentMethodId,
        account_id: store.$state.accountId || undefined,
    }
    const orderResponse = await createHardwareOrder(orderParams)
    state.equipmentComplete = true
    return orderResponse
}

const handleSubscriptionChange = async() => {
    if (!state.coreSubscription) {
        throw new Error('No core subscription found')
    }
    if (!user.user?.account_id) {
        throw new Error('Must be logged in to change your membership plan')
    }
    if (!cart.selectedPlan?.stripe_price_id) {
        throw new Error('No plan selected')
    }

    const orderParams = {
        items: [
            {
                price_id: cart.selectedPlan.stripe_price_id,
                quantity: 1,
            },
        ],
    }

    state.isLoading = true
    state.claimScopeFn = null

    try {
        const response = await user.authenticatedRequest(`/accounts/v2/${user.user.account_id}/subscriptions/${state.coreSubscription.id}`, {
            method: 'PUT',
            body: JSON.stringify(orderParams),
        })
        state.claimScopeFn = null
        // handle any equipment purchases
        if (cart.requireShipping) {
            await handleEquipmentPurchase(state.selectedPaymentMethodId || store.$state.paymentMethodId)
        }
        state.upgradeComplete = true
    } catch (e: any|WINApiError) {
        if (e?.id === 'UnauthorizedScope') {
            state.showScopeClaimModal = true
            state.claimScopeFn = async() => await handleSubscriptionChange()
        } else {
            state.error = 'An error occurred while changing your membership plan.'
        }
    } finally {
        state.isLoading = false
    }
}

const handleAttatchPaymentMethod = async(paymentMethodId: string): Promise<any> => {
    state.isLoading = true
    state.claimScopeFn = null
    try {
        await user.attachPaymentMethod(paymentMethodId)
        const paymentMethods = await user.fetchPaymentMethods()
        state.paymentMethods = paymentMethods
        state.selectedPaymentMethodId = paymentMethodId
        state.claimScopeFn = null
        state.showPaymentForm = false
        return paymentMethodId
    } catch (e: any|WINApiError) {
        if (e?.id === 'UnauthorizedScope') {
            state.showScopeClaimModal = true
            state.claimScopeFn = async() => await handleAttatchPaymentMethod(paymentMethodId)
        } else {
            state.error = 'An error occurred while adding your payment method.'
        }
        console.error(e)
    } finally {
        state.isLoading = false
    }
}

const prePaymentValidator = async(): Promise<boolean> => {
    return true
}
</script>
